/**
 * Administration > Roles (M2 W5b). Read-only list of the role catalog, each row showing its
 * granted permission keys as chips. Roles are immutable in V1 (the API exposes no write), so
 * this screen is purely informational. Non-PHI.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SkyDataGrid,
  SkyBadge,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { ApiProblem } from "../../lib/api";
import { listRoles, type AdminRole } from "../../lib/adminApi";
import { deriveNavigation } from "../../../shared/rbac/deriveNavigation";
import { NAV_SECTIONS } from "../../shell/nav";
import { PageError } from "../../components/PageError";
import { AdminPage } from "./adminShared";

// nav id -> human label, taken from the SAME nav config the sidebar renders, so the Roles copy and the
// actual sidebar fan-out can never drift. Built once.
const NAV_LABELS: Record<string, string> = Object.fromEntries(
  NAV_SECTIONS.flatMap((s) => s.items.map((i) => [i.id, i.label])),
);

/**
 * The nav areas a role's permissions ACTUALLY reveal, driven by the shared deriveNavigation mapping
 * (the same pure function the sidebar + /api/v1/me use). This makes the per-role copy describe the real
 * fan-out (e.g. viewParticipant + viewRoster reveals Participants, Vitals, Encounters, Care Plans,
 * Medications, Population Health, Quality, Care Gaps ...), instead of an understated hand-written summary.
 */
function revealedNavLabels(permissions: string[]): string[] {
  const map: Record<string, boolean> = Object.fromEntries(permissions.map((p) => [p, true]));
  return deriveNavigation(map).map((id) => NAV_LABELS[id] ?? id);
}

export function AdminRoles() {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation counter: two concurrent load() calls (a double-clicked Try-again, or a StrictMode
  // double-invoke) can resolve out of order, and a late error could otherwise revert a shown
  // success. Stamp each call and gate every setState on it being the latest, so only the newest
  // result applies.
  const loadGenRef = useRef(0);

  // load is a stable useCallback so the effect depends on IT (not a bare []), and a Try-again affordance
  // can re-invoke the exact same fetch — a transient failure is recoverable in place, never terminal.
  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    listRoles()
      .then(({ data }) => {
        if (gen === loadGenRef.current) setRoles(data);
      })
      .catch((e: unknown) => {
        if (gen !== loadGenRef.current) return;
        setError(e instanceof ApiProblem ? e.problem.title : "Failed to load roles.");
        setRoles([]);
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns: SkyDataGridColumn<AdminRole>[] = [
    {
      key: "label",
      header: "Role",
      width: "12%",
      renderCell: (_v, row) => (
        <div className="flex items-center gap-2">
          <SkyText weight="semibold">{row.label}</SkyText>
          {row.isSystem && (
            <SkyBadge appearance="tint" color="subtle" size="small">
              System
            </SkyBadge>
          )}
        </div>
      ),
    },
    { key: "name", header: "Key", width: "12%" },
    {
      key: "description",
      header: "Description",
      width: "20%",
      renderCell: (_v, row) => row.description ?? <SkyText size={200}>No description</SkyText>,
    },
    {
      // Drive the role's "what it reveals" copy from the REAL permission -> nav mapping (deriveNavigation),
      // so the description and the resulting sidebar tell the same story (no understated hand-written
      // summary). An empty permission set honestly reveals nothing.
      key: "navAccess",
      header: "Reveals (sidebar)",
      width: "26%",
      renderCell: (_v, row) => {
        const labels = revealedNavLabels(row.permissions);
        if (labels.length === 0) return <SkyText size={200}>Reveals no nav areas</SkyText>;
        return (
          <div className="flex flex-col gap-1">
            <SkyText size={200} weight="medium">
              {`Reveals ${labels.length} nav ${labels.length === 1 ? "area" : "areas"}`}
            </SkyText>
            <SkyText size={100} color="muted">
              {labels.join(", ")}
            </SkyText>
          </div>
        );
      },
    },
    {
      key: "permissions",
      header: "Permissions",
      width: "30%",
      renderCell: (_v, row) => {
        if (row.permissions.length === 0)
          return <SkyText size={200}>No permissions</SkyText>;
        return (
          <div className="flex flex-wrap gap-1">
            {row.permissions.map((p) => (
              <SkyBadge key={p} appearance="tint" color="brand" size="small">
                {p}
              </SkyBadge>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <AdminPage
      crumbs={[
        { key: "home", label: "skyCare", to: "/" },
        { key: "admin", label: "Administration" },
        { key: "roles", label: "Roles" },
      ]}
      title="Roles"
      description="The role catalog: the permissions each role grants and the sidebar areas they actually reveal (derived from the live permission-to-nav mapping, so the copy matches the real fan-out). Read-only in this release."
      requires={["viewRoles", "manageRoles"]}
    >
      {error && <PageError title="Could not load roles" message={error} onRetry={load} />}
      <SkyDataGrid<AdminRole>
        data={roles}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        emptyMessage="No roles found"
      />
    </AdminPage>
  );
}
